import React from "react";
import styled from "@emotion/styled";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

const PreviewContainer = styled("div")(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "8px",
  overflow: "hidden",
  fontFamily: "Roboto, Arial, sans-serif",
}));

const VideoPlayer = styled("div")(({ theme }) => ({
  width: "100%",
  aspectRatio: "16 / 9",
  backgroundColor: "#000000",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "& img, & video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const PlayButton = styled("div")(({ theme }) => ({
  position: "absolute",
  width: "68px",
  height: "48px",
  backgroundColor: "rgba(255, 0, 0, 0.9)",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s",
  zIndex: 1,

  "&:hover": {
    backgroundColor: "rgba(204, 0, 0, 1)",
  },

  "&::after": {
    content: '""',
    width: 0,
    height: 0,
    borderLeft: "12px solid white",
    borderTop: "8px solid transparent",
    borderBottom: "8px solid transparent",
    marginLeft: "4px",
  },
}));

const Thumbnail = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  backgroundColor: "#F0F0F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#737373",
  fontSize: "14px",
  flexDirection: "column",
  gap: "8px",
}));

const VideoInfo = styled("div")(({ theme }) => ({
  padding: "12px",
}));

const VideoTitle = styled("div")(({ theme }) => ({
  fontSize: "18px",
  fontWeight: "500",
  lineHeight: "1.4",
  color: "#0F0F0F",
  marginBottom: "4px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const VideoStats = styled("div")(({ theme }) => ({
  fontSize: "14px",
  color: "#606060",
  marginBottom: "12px",
}));

const ChannelInfo = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "12px",
}));

const ChannelAvatar = styled("div")(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: "#FF0000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontWeight: "600",
  fontSize: "16px",
  flexShrink: 0,
}));

const ChannelDetails = styled("div")(({ theme }) => ({
  flex: 1,

  "& .channel_name": {
    fontSize: "14px",
    fontWeight: "500",
    color: "#0F0F0F",
    marginBottom: "2px",
  },

  "& .subscriber_count": {
    fontSize: "12px",
    color: "#606060",
  },
}));

const SubscribeButton = styled("button")(({ theme }) => ({
  backgroundColor: "#0F0F0F",
  color: "#FFFFFF",
  border: "none",
  borderRadius: "18px",
  padding: "10px 16px",
  fontSize: "14px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "background-color 0.2s",

  "&:hover": {
    backgroundColor: "#272727",
  },
}));

const ActionButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  marginBottom: "12px",
}));

const ActionButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 16px",
  backgroundColor: "#F2F2F2",
  border: "none",
  borderRadius: "18px",
  fontSize: "14px",
  fontWeight: "500",
  color: "#0F0F0F",
  cursor: "pointer",
  transition: "background-color 0.2s",

  "&:hover": {
    backgroundColor: "#E5E5E5",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

const Divider = styled("div")(({ theme }) => ({
  height: "1px",
  backgroundColor: "#E5E5E5",
  margin: "12px 0",
}));

const Description = styled("div")(({ theme }) => ({
  fontSize: "14px",
  lineHeight: "1.6",
  color: "#0F0F0F",
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  backgroundColor: "#F2F2F2",
  padding: "12px",
  borderRadius: "8px",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",

  "& .show_more": {
    color: "#065FD4",
    cursor: "pointer",
    fontWeight: "500",
  },
}));

const YouTubePreview = ({ modalData }) => {
  const channelName = modalData?.channelName || "Channel";
  const videoTitle =
    modalData?.title || modalData?.caption?.substring(0, 100) || "Video Title";
  const views = modalData?.views || 0;
  const uploadDate = modalData?.uploadDate || "Just now";
  const description = modalData?.caption || "";
  const likes = modalData?.likes || 0;
  const subscribers = modalData?.subscribers || "0";
  const thumbnailUrl = modalData?.imageUrl;
  const videoUrl = modalData?.videoUrl;

  // Determine if media is video
  const isVideoFile = (value) => {
  if (!value) return false;
    return /\.(mp4|mov|avi|webm|mkv)(\?|#|$)/i.test(value);
  };
  const isVideo = isVideoFile(videoUrl) || isVideoFile(thumbnailUrl);
  const mediaUrl = videoUrl || thumbnailUrl;

  return (
    <PreviewContainer>
      <VideoPlayer>
        {mediaUrl ? (
          <>
            {isVideo ? (
              <video>
                <source src={mediaUrl} type="video/mp4" />
              </video>
            ) : (
              <img src={mediaUrl} alt="Video thumbnail" />
            )}
            <PlayButton />
          </>
        ) : (
          <Thumbnail>
            {/* <span> Video</span> */}
            <span>No Preview Available</span>
          </Thumbnail>
        )}
      </VideoPlayer>

      <VideoInfo>
        <VideoTitle>{description}</VideoTitle>

        <VideoStats>
          {views > 0 ? `${views.toLocaleString()} views` : "0 views"} •{" "}
          {uploadDate}
        </VideoStats>

        <ChannelInfo>
          <ChannelAvatar>{channelName.charAt(0)}</ChannelAvatar>
          <ChannelDetails>
            <div className="channel_name">{channelName}</div>
            <div className="subscriber_count">
              {typeof subscribers === "number"
                ? subscribers.toLocaleString()
                : subscribers}{" "}
              subscribers
            </div>
          </ChannelDetails>
          <SubscribeButton>Subscribe</SubscribeButton>
        </ChannelInfo>

        <ActionButtons>
          <ActionButton>
            <ThumbUpOutlinedIcon />
            {likes > 0 ? likes.toLocaleString() : 0}
          </ActionButton>
          <ActionButton>
            <ThumbDownOutlinedIcon />
          </ActionButton>
          <ActionButton>
            <ReplyOutlinedIcon />
            Share
          </ActionButton>
          <ActionButton>
            <PlaylistAddIcon />
            Save
          </ActionButton>
          <ActionButton>
            <MoreHorizIcon />
          </ActionButton>
        </ActionButtons>
        {/* 
        {description && (
          <>
            <Divider />
            <Description>
              {description}
              <div className="show_more">...more</div>
            </Description>
          </>
        )} */}
      </VideoInfo>
    </PreviewContainer>
  );
};

export default YouTubePreview;
